import React from 'react'

import type { ConnectedAppStatus } from '../../types'
import { DOWNLOAD_CENTER_URL } from '../../constants'

import STATUS_SWITCH from '@public/assets/heartbeat/status_switch.gif'
import CHROME_OFF from '@public/assets/heartbeat/chrome_off.svg'
import CHROME_ON from '@public/assets/heartbeat/chrome_on.svg'
import CHROME_UNINSTALL from '@public/assets/heartbeat/chrome_uninstall_only.svg'
import DESKTOP_AGENT_OFF from '@public/assets/heartbeat/desktop_agent_off.svg'
import DESKTOP_AGENT_ON from '@public/assets/heartbeat/desktop_agent_on.svg'
import DESKTOP_AGENT_UNINSTALL from '@public/assets/heartbeat/desktop_agent_uninstall.svg'
import EDGE_OFF from '@public/assets/heartbeat/edge_off.svg'
import EDGE_ON from '@public/assets/heartbeat/edge_on.svg'
import EDGE_UNINSTALL from '@public/assets/heartbeat/edge_uninstall_only.svg'
import QUESTION_ICON from '@public/assets/button/question-solid.svg'

type ChecklistStatus = 'loading' | 'online' | 'offline' | 'uninstall'

type ChecklistStatusItemProps = {
  icon: string
  label: string
}

type CompletionChecklistStepProps = {
  chromeExtensionStatus: ConnectedAppStatus
  desktopAgentStatus: ConnectedAppStatus
  edgeExtensionStatus: ConnectedAppStatus
}

const STATUS_LABELS: Record<ChecklistStatus, string> = {
  loading: 'Checking',
  online: 'Installed',
  offline: 'Off',
  uninstall: 'Not Installed',
}

const STATUS_ICONS = {
  agent: {
    loading: STATUS_SWITCH,
    online: DESKTOP_AGENT_ON,
    offline: DESKTOP_AGENT_OFF,
    uninstall: DESKTOP_AGENT_UNINSTALL,
  },
  chrome: {
    loading: STATUS_SWITCH,
    online: CHROME_ON,
    offline: CHROME_OFF,
    uninstall: CHROME_UNINSTALL,
  },
  edge: {
    loading: STATUS_SWITCH,
    online: EDGE_ON,
    offline: EDGE_OFF,
    uninstall: EDGE_UNINSTALL,
  },
} satisfies Record<string, Record<ChecklistStatus, string>>

const getChecklistStatus = (status: ConnectedAppStatus): ChecklistStatus => {
  if (
    status === 'loading' ||
    status === 'online' ||
    status === 'offline' ||
    status === 'uninstall'
  ) {
    return status
  }

  return 'offline'
}

const ChecklistStatusItem = ({ icon, label }: ChecklistStatusItemProps) => (
  <div className="flex items-center gap-1.5 text-sm text-[#666]">
    <span>{label}</span>
    <img alt="" className="h-5 w-5 object-contain" src={icon} />
  </div>
)

const CompletionChecklistStep = ({
  chromeExtensionStatus,
  desktopAgentStatus,
  edgeExtensionStatus,
}: CompletionChecklistStepProps) => {
  const agentStatus = getChecklistStatus(desktopAgentStatus)
  const chromeStatus = getChecklistStatus(chromeExtensionStatus)
  const edgeStatus = getChecklistStatus(edgeExtensionStatus)

  return (
    <div className="max-w-[900px]">
      <h2 className="text-lg font-semibold text-black">Completion Checklist</h2>
      <h3 className="mt-4 font-semibold">Desktop Agent</h3>
      <div className="mt-3 space-y-2">
        <ChecklistStatusItem
          icon={STATUS_ICONS.agent.online}
          label="Tracking configured (15 file types selected)"
        />
        <ChecklistStatusItem
          icon={STATUS_ICONS.agent[agentStatus]}
          label={`Tracking: ${
            agentStatus === 'online' ? 'On' : STATUS_LABELS[agentStatus]
          }`}
        />
      </div>
      <h3 className="mt-5 font-semibold">Web Extension</h3>
      <div className="mt-3 space-y-2">
        <ChecklistStatusItem
          icon={STATUS_ICONS.chrome[chromeStatus]}
          label={`Chrome Extension: ${STATUS_LABELS[chromeStatus]}`}
        />
        <div className="flex items-center gap-2">
          <ChecklistStatusItem
            icon={STATUS_ICONS.edge[edgeStatus]}
            label={`Edge Extension: ${STATUS_LABELS[edgeStatus]}`}
          />
          {edgeStatus === 'uninstall' && (
            <a
              className="text-sm text-[#7255e9] hover:underline"
              href={DOWNLOAD_CENTER_URL}
              rel="noreferrer"
              target="_blank"
            >
              Open Edge to install (Optional)
            </a>
          )}
        </div>
      </div>
      {chromeStatus === 'uninstall' && (
        <a
          className="mt-2 inline-flex text-sm text-[#7255e9] hover:underline"
          href={DOWNLOAD_CENTER_URL}
          rel="noreferrer"
          target="_blank"
        >
          Open Chrome to install (Optional)
        </a>
      )}
      <div className="mt-4 flex max-w-[780px] items-center rounded-lg border border-[#d9d9d9] bg-[#f5f5f5] px-5 py-2 text-[#666]">
        <img alt="" className="mr-2.5 size-[17.5px]" src={QUESTION_ICON} />
        <div>
          <p className="text-sm font-bold leading-[1.42] tracking-normal text-[#333]">
            Work across Chrome and Edge?
          </p>
          <p className="text-sm leading-[1.42] tracking-normal">
            We recommend installing the extension in both browsers to ensure all
            billable activity is captured.
          </p>
        </div>
      </div>
    </div>
  )
}

export default CompletionChecklistStep
